import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Capacitor} from '@capacitor/core';
import {App as CapacitorApp, URLOpenListenerEvent} from '@capacitor/app';
import {useHistory, useLocation} from 'react-router';
import {useIonRouter} from '@ionic/react';
import {useCustomerStore} from '../../redux/customers';
import {useAuthStore} from '../../redux/auth';
import {handleDeepLinkUrl, requiresCustomerContext, hasCustomerInList} from '../common/deepLinkHandler';

const DeepLinkListener: React.FC = () => {
    const history = useHistory();
    const ionRouter = useIonRouter();
    const location = useLocation();

    const {isAuthed} = useAuthStore();
    const customerStore = useCustomerStore();
    const {customers, isFetching, setSelectedId: setSelectedCustomerId} = customerStore;

    const [pendingUrl, setPendingUrl] = useState<string | null>(null);
    const hasHandledInitialWebUrl = useRef<boolean>(false);
    const hasCheckedLaunchUrl = useRef<boolean>(false);

    const isWeb = useMemo(() => Capacitor.getPlatform() === 'web', []);

    useEffect(() => {
        if (isWeb) return;

        let listenerHandle: { remove: () => Promise<void> } | null = null;

        const setup = async () => {
            listenerHandle = await CapacitorApp.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
                if (!event?.url) return;
                setPendingUrl(event.url);
            });

            if (!hasCheckedLaunchUrl.current) {
                hasCheckedLaunchUrl.current = true;
                const launchData = await CapacitorApp.getLaunchUrl();
                if (launchData?.url) {
                    setPendingUrl(launchData.url);
                }
            }
        };

        setup().catch((error) => {
            console.error('Failed to initialize native deep link listener:', error);
        });

        return () => {
            if (listenerHandle) {
                listenerHandle.remove().catch((error) => {
                    console.error('Failed to remove native deep link listener:', error);
                });
            }
        };
    }, [isWeb]);

    useEffect(() => {
        if (!isWeb) return;
        if (hasHandledInitialWebUrl.current) return;

        hasHandledInitialWebUrl.current = true;
        setPendingUrl(window.location.href);
    }, [isWeb]);

    useEffect(() => {
        if (!pendingUrl) return;

        const needsCustomers = requiresCustomerContext(pendingUrl);
        if (needsCustomers) {
            if (!isAuthed || !customers || customers.length === 0) {
                return;
            }

            if (!hasCustomerInList(pendingUrl, customers) && isFetching) {
                return;
            }
        }

        handleDeepLinkUrl({
            url: pendingUrl,
            customers,
            setSelectedCustomerId,
            history,
            ionRouter,
            currentLocation: location.pathname,
        })
            .finally(() => {
                setPendingUrl(null);
            });
    }, [pendingUrl, isAuthed, customers, isFetching, setSelectedCustomerId, history, ionRouter, location.pathname]);

    return null;
};

export default DeepLinkListener;
